/**
 * ML-Enhanced Load Recommendations
 * 
 * Uses TensorFlow.js to predict optimal load based on:
 * - User's historical performance patterns  
 * - Today's readiness score
 * - Recent RPE trends
 * - Intra-session fatigue accumulation
 * - Exercise-specific learning
 * 
 * Falls back to rule-based system for new users (<20 sessions).
 */

// TYPE-ONLY import: TensorFlow.js must not be in the main bundle. This module
// is statically imported by dataLoader / smartWorkoutGenerator /
// unifiedRecommendation, so a value import here dragged ~1MB of tfjs into every
// page load AND ran tf.setBackend() as a module side effect on boot — for a
// model whose output nothing consumes (see LAUNCH_FLAGS.tensorflowModels).
// Value access now goes through loadTf(), which is only reachable when the
// flag is on.
import type * as tf from '@tensorflow/tfjs';

let tfModule: typeof tf | null = null;

/**
 * Lazily load TensorFlow.js and pin it to the CPU backend. Prevents WebGL
 * initialization errors on browsers without GPU support (and the noisy console
 * errors that come with them). CPU is fine for models this small — just slower.
 *
 * Only called from flag-gated paths, so with the flag off tfjs is never fetched.
 */
async function loadTf(): Promise<typeof tf> {
  if (tfModule) return tfModule;
  const mod = await import('@tensorflow/tfjs');
  if (typeof window !== 'undefined') {
    await mod.setBackend('cpu').catch(() => { /* fallback already cpu */ });
    mod.env().set('PROD', true);
  }
  tfModule = mod;
  return mod;
}
import { getSessionHistory } from './sessionStorage';
import { getRecoveryHistory } from './recoveryCalculator';
import { roundToNearestPlate } from './plateMath';
import { getHybridRecommendation, type AccessoryRecommendation } from './accessoryAdaptive';
import { storage } from './storage';
import type { PTSessionLog, SessionLog } from './sessionStorage';
import { LAUNCH_FLAGS } from './launchFlags';

// Model storage key
const MODEL_KEY = 'opmode-load-model';
const MODEL_META_KEY = 'opmode-load-model-meta';

// Singleton model instance
let loadModel: tf.LayersModel | null = null;
let modelMeta: ModelMeta | null = null;

// Training state to prevent race conditions
let isTraining = false;
let trainingPromise: Promise<{ success: boolean; message: string; metrics?: { loss: number; exerciseCount: number } }> | null = null;

interface ModelMeta {
  trainedAt: string;
  sessionCount: number;
  exerciseCount: number;
  avgLoss: number;
}

interface ExerciseFeatures {
  // Normalized features for ML input
  lastWeight: number;          // Normalized 0-1 (relative to max)
  lastReps: number;            // Normalized 0-1 (1-20 range)
  lastRPE: number;             // Normalized 0-1 (1-10 range)
  avgRPELast3: number;         // Normalized 0-1
  targetReps: number;          // Normalized 0-1
  readiness: number;           // Normalized 0-1
  setsCompletedToday: number;  // Normalized 0-1 (0-20 range)
  daysSinceExercise: number;   // Normalized 0-1 (0-14 range)
  progressionRate: number;     // Normalized -1 to 1 (decline vs progress)
  volumeTrend: number;         // Normalized 0-1
}

/**
 * Train ML model on user's historical performance data
 * Returns training metrics
 */
export async function trainLoadModel(): Promise<{
  success: boolean;
  message: string;
  metrics?: { loss: number; exerciseCount: number };
}> {
  if (!LAUNCH_FLAGS.tensorflowModels) {
    return { success: false, message: 'Neural-net load model is disabled (LAUNCH_FLAGS.tensorflowModels)' };
  }
  console.log('� Training load recommendation model...');
  
  const sessions = await getSessionHistory();
  
  if (sessions.length < 20) {
    return {
      success: false,
      message: `Need at least 20 sessions for ML training. Current: ${sessions.length}`,
    };
  }

  try {
    // Prepare training data from exercise history
    const { inputs, outputs, exerciseCount } = prepareTrainingData(sessions);
    
    if (inputs.length < 50) {
      return {
        success: false,
        message: `Need more exercise data points. Current: ${inputs.length}, need 50+`,
      };
    }

    console.log(` Training on ${inputs.length} data points from ${exerciseCount} exercises`);

    const tf = await loadTf();

    // Build neural network
    const model = tf.sequential({
      layers: [
        tf.layers.dense({ 
          inputShape: [10], 
          units: 32, 
          activation: 'relu',
          kernelRegularizer: tf.regularizers.l2({ l2: 0.01 })
        }),
        tf.layers.dropout({ rate: 0.2 }),
        tf.layers.dense({ units: 16, activation: 'relu' }),
        tf.layers.dropout({ rate: 0.1 }),
        tf.layers.dense({ units: 8, activation: 'relu' }),
        tf.layers.dense({ units: 1, activation: 'linear' }), // Output: weight multiplier
      ],
    });

    model.compile({
      optimizer: tf.train.adam(0.001),
      loss: 'meanSquaredError',
      metrics: ['mae'],
    });

    // Convert to tensors
    const inputTensor = tf.tensor2d(inputs);
    const outputTensor = tf.tensor2d(outputs.map(o => [o]));

    // Train
    const history = await model.fit(inputTensor, outputTensor, {
      epochs: 100,
      batchSize: 16,
      validationSplit: 0.2,
      verbose: 0,
      callbacks: {
        onEpochEnd: (epoch, logs) => {
          if (epoch % 25 === 0) {
            console.log(`  Epoch ${epoch}: loss=${logs?.loss.toFixed(4)}, val_loss=${logs?.val_loss?.toFixed(4)}`);
          }
        },
      },
    });

    // Get final loss
    const finalLoss = history.history.loss[history.history.loss.length - 1] as number;

    // Save model to IndexedDB
    await model.save(`indexeddb://${MODEL_KEY}`);
    
    // Save metadata
    modelMeta = {
      trainedAt: new Date().toISOString(),
      sessionCount: sessions.length,
      exerciseCount,
      avgLoss: finalLoss,
    };
    storage.set(MODEL_META_KEY, JSON.stringify(modelMeta));

    // Store in memory
    loadModel = model;

    // Cleanup
    inputTensor.dispose();
    outputTensor.dispose();

    console.log(` Load model trained successfully (loss: ${finalLoss.toFixed(4)})`);

    return {
      success: true,
      message: `Model trained on ${inputs.length} data points`,
      metrics: { loss: finalLoss, exerciseCount },
    };
  } catch (error) {
    console.error(' Model training failed:', error);
    return {
      success: false,
      message: `Training failed: ${error}`,
    };
  }
}

/**
 * Safe training wrapper that prevents race conditions
 * Tracks training state and ensures only one training operation runs at a time
 */
async function trainLoadModelSafe(): Promise<{
  success: boolean;
  message: string;
  metrics?: { loss: number; exerciseCount: number };
}> {
  // Prevent concurrent training
  if (isTraining) {
    return {
      success: false,
      message: 'Training already in progress',
    };
  }

  isTraining = true;
  try {
    const result = await trainLoadModel();
    return result;
  } finally {
    isTraining = false;
    trainingPromise = null; // Clear the promise so future calls can train again
  }
}

/**
 * Load saved model from IndexedDB
 */
export async function loadSavedModel(): Promise<boolean> {
  if (!LAUNCH_FLAGS.tensorflowModels) return false;
  try {
    const tf = await loadTf();
    loadModel = await tf.loadLayersModel(`indexeddb://${MODEL_KEY}`);
    const metaStr = storage.get(MODEL_META_KEY);
    if (metaStr) {
      modelMeta = JSON.parse(metaStr);
    }
    console.log(' Load model restored from IndexedDB');
    return true;
  } catch (error) {
    console.log('ℹ No saved load model found');
    return false;
  }
}

/**
 * Check if ML model is available and trained
 */
export function isMLModelReady(): boolean {
  return LAUNCH_FLAGS.tensorflowModels && loadModel !== null;
}

/**
 * Get model training metadata
 */
export function getModelMeta(): ModelMeta | null {
  return modelMeta;
}

/**
 * Get ML-powered load recommendation
 * 
 * Priority:
 * 1. Use trained TensorFlow model if available (20+ sessions)
 * 2. Fall back to rule-based hybrid recommendation
 */
export async function getMLLoadRecommendation(
  exerciseName: string,
  targetReps: number,
  periodizationWeight: number,
  readinessMultiplier: number = 1.0,
  setsCompletedToday: number = 0
): Promise<AccessoryRecommendation & { usedML: boolean; mlConfidence?: number }> {
  // Neural-net path disabled — go straight to the rule-based/statistical path
  // without loading session history or touching TensorFlow. See LAUNCH_FLAGS.
  if (!LAUNCH_FLAGS.tensorflowModels) {
    const recommendation = await getHybridRecommendation(
      exerciseName,
      targetReps,
      periodizationWeight,
      readinessMultiplier
    );
    return { ...recommendation, usedML: false };
  }

  const sessions = await getSessionHistory();

  // Check if we should use ML
  if (sessions.length < 20 || !loadModel) {
    // Try to load saved model if we have enough data
    if (sessions.length >= 20 && !loadModel && !isTraining) {
      const loaded = await loadSavedModel();
      if (!loaded) {
        // Model not available, train it (with proper race condition handling)
        if (!trainingPromise) {
          trainingPromise = trainLoadModelSafe();
        }
        // Don't await here - let training run in background but track it
        trainingPromise.catch(console.error);
      }
    }

    // If training is in progress from another call, wait for it (max 5 seconds)
    if (trainingPromise && !loadModel) {
      try {
        await Promise.race([
          trainingPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Training timeout')), 5000))
        ]);
      } catch {
        // Training took too long or failed, use fallback
      }
    }

    if (!loadModel) {
      // Fallback to rule-based system
      const recommendation = await getHybridRecommendation(
        exerciseName,
        targetReps,
        periodizationWeight,
        readinessMultiplier
      );
      return { ...recommendation, usedML: false };
    }
  }

  try {
    // Get recovery data
    const recoveryData = await getRecoveryHistory();
    const todayRecovery = recoveryData.length > 0 ? recoveryData[recoveryData.length - 1] : null;
    const readiness = todayRecovery?.readinessScore ?? 70;

    // Find exercise history
    const exerciseHistory = extractExerciseHistory(sessions, exerciseName);

    if (exerciseHistory.length < 2) {
      // Not enough exercise-specific data, use rule-based
      const recommendation = await getHybridRecommendation(
        exerciseName,
        targetReps,
        periodizationWeight,
        readinessMultiplier
      );
      return { ...recommendation, usedML: false };
    }

    // Prepare features for ML prediction
    const features = prepareExerciseFeatures(
      exerciseHistory,
      targetReps,
      readiness,
      setsCompletedToday
    );

    // ML Prediction with proper tensor cleanup on error
    let inputTensor: tf.Tensor | null = null;
    let prediction: tf.Tensor | null = null;
    let multiplier: number;

    try {
      const tfLib = await loadTf();
      inputTensor = tfLib.tensor2d([Object.values(features)]);
      prediction = loadModel.predict(inputTensor) as tf.Tensor;
      multiplier = (await prediction.data())[0];
    } finally {
      // Cleanup tensors even if prediction.data() throws
      inputTensor?.dispose();
      prediction?.dispose();
    }

    // Apply multiplier to last weight
    const lastWeight = exerciseHistory[exerciseHistory.length - 1].weight;
    let predictedWeight = lastWeight * multiplier;

    // Apply readiness adjustment
    predictedWeight *= readinessMultiplier;

    // Apply intra-session fatigue (each set reduces by ~1.5%)
    const fatigueMultiplier = Math.max(0.88, 1 - (setsCompletedToday * 0.015));
    predictedWeight *= fatigueMultiplier;

    // Round to nearest plate
    const finalWeight = roundToNearestPlate(predictedWeight);

    // Calculate confidence based on data quality
    const confidence = Math.min(0.95, 0.6 + (exerciseHistory.length / 50) * 0.35);

    // Build reasoning (concise)
    const delta = ((finalWeight - lastWeight) / lastWeight) * 100;
    const deltaStr = delta >= 0 ? `+${delta.toFixed(0)}%` : `${delta.toFixed(0)}%`;
    const reasoning = setsCompletedToday > 0
      ? ` ML: ${finalWeight} lbs (fatigue adj.)`
      : ` ML: ${finalWeight} lbs (${deltaStr} from last)`;

    return {
      suggestedWeight: finalWeight,
      suggestedReps: targetReps,
      confidence: confidence > 0.75 ? 'high' : confidence > 0.5 ? 'medium' : 'low',
      reasoning,
      lastPerformance: {
        weight: lastWeight,
        reps: exerciseHistory[exerciseHistory.length - 1].reps,
        rpe: exerciseHistory[exerciseHistory.length - 1].rpe,
        date: new Date(exerciseHistory[exerciseHistory.length - 1].date).toLocaleDateString(),
      },
      usedML: true,
      mlConfidence: confidence,
    };
  } catch (error) {
    console.error('ML prediction failed, using rule-based:', error);
    const recommendation = await getHybridRecommendation(
      exerciseName,
      targetReps,
      periodizationWeight,
      readinessMultiplier
    );
    return { ...recommendation, usedML: false };
  }
}

// ============================================================================
// PRIVATE HELPERS
// ============================================================================

/**
 * Prepare training data from session history
 */
function prepareTrainingData(sessions: SessionLog[]): {
  inputs: number[][];
  outputs: number[];
  exerciseCount: number;
} {
  const inputs: number[][] = [];
  const outputs: number[] = [];
  const exercisesSeen = new Set<string>();

  // Group by exercise to track progression
  const exerciseData = new Map<string, Array<{
    weight: number;
    reps: number;
    rpe: number;
    date: Date;
  }>>();

  sessions.forEach(session => {
    if (session.sessionType !== 'pt' || !('exercises' in session)) return;
    const ptSession = session as PTSessionLog;

    ptSession.exercises.forEach(ex => {
      if (!ex.sets || ex.sets.length === 0) return;

      const completedSets = ex.sets.filter(s => s.weight > 0 && s.reps > 0);
      if (completedSets.length === 0) return;

      const avgWeight = completedSets.reduce((sum, s) => sum + s.weight, 0) / completedSets.length;
      const avgReps = completedSets.reduce((sum, s) => sum + s.reps, 0) / completedSets.length;
      const avgRPE = completedSets.reduce((sum, s) => sum + (s.rpe || 7), 0) / completedSets.length;

      const exerciseName = ex.exerciseName.toLowerCase();
      exercisesSeen.add(exerciseName);

      if (!exerciseData.has(exerciseName)) {
        exerciseData.set(exerciseName, []);
      }

      exerciseData.get(exerciseName)!.push({
        weight: avgWeight,
        reps: avgReps,
        rpe: avgRPE,
        date: new Date(session.date),
      });
    });
  });

  // Create training samples from consecutive performances
  exerciseData.forEach((history, exerciseName) => {
    if (history.length < 3) return; // Need at least 3 performances

    // Sort by date
    history.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Find max weight for normalization
    const maxWeight = Math.max(...history.map(h => h.weight));

    for (let i = 2; i < history.length; i++) {
      const prev = history[i - 1];
      const prevPrev = history[i - 2];
      const current = history[i];

      // Calculate features
      const features: number[] = [
        prev.weight / maxWeight,                                    // lastWeight normalized
        prev.reps / 20,                                             // lastReps normalized
        prev.rpe / 10,                                              // lastRPE normalized
        ((prev.rpe + prevPrev.rpe) / 2) / 10,                       // avgRPELast2 normalized
        current.reps / 20,                                          // targetReps (from actual)
        0.7,                                                        // readiness (estimated)
        0,                                                          // setsCompletedToday (unknown)
        Math.min(14, (current.date.getTime() - prev.date.getTime()) / (1000 * 60 * 60 * 24)) / 14, // daysSince
        (current.weight - prev.weight) / prev.weight,               // progressionRate
        (prev.weight * prev.reps) / (prevPrev.weight * prevPrev.reps) - 1, // volumeTrend
      ];

      // Output: weight multiplier (current/previous)
      const output = current.weight / prev.weight;

      // Only include reasonable progressions (-20% to +20%)
      if (output >= 0.8 && output <= 1.2) {
        inputs.push(features);
        outputs.push(output);
      }
    }
  });

  return { inputs, outputs, exerciseCount: exercisesSeen.size };
}

/**
 * Extract exercise-specific history from sessions
 */
function extractExerciseHistory(
  sessions: SessionLog[],
  exerciseName: string
): Array<{ weight: number; reps: number; rpe: number; date: Date }> {
  const history: Array<{ weight: number; reps: number; rpe: number; date: Date }> = [];
  const nameLower = exerciseName.toLowerCase();

  sessions.forEach(session => {
    if (session.sessionType !== 'pt' || !('exercises' in session)) return;
    const ptSession = session as PTSessionLog;

    ptSession.exercises.forEach(ex => {
      if (ex.exerciseName.toLowerCase() !== nameLower) return;
      if (!ex.sets || ex.sets.length === 0) return;

      const completedSets = ex.sets.filter(s => s.weight > 0 && s.reps > 0);
      if (completedSets.length === 0) return;

      const avgWeight = completedSets.reduce((sum, s) => sum + s.weight, 0) / completedSets.length;
      const avgReps = completedSets.reduce((sum, s) => sum + s.reps, 0) / completedSets.length;
      const avgRPE = completedSets.reduce((sum, s) => sum + (s.rpe || 7), 0) / completedSets.length;

      history.push({
        weight: avgWeight,
        reps: avgReps,
        rpe: avgRPE,
        date: new Date(session.date),
      });
    });
  });

  // Sort by date
  history.sort((a, b) => a.date.getTime() - b.date.getTime());

  return history.slice(-10); // Last 10 performances
}

/**
 * Prepare normalized features for ML prediction
 */
function prepareExerciseFeatures(
  history: Array<{ weight: number; reps: number; rpe: number; date: Date }>,
  targetReps: number,
  readiness: number,
  setsCompletedToday: number
): ExerciseFeatures {
  const last = history[history.length - 1];
  const prev = history.length >= 2 ? history[history.length - 2] : last;
  const maxWeight = Math.max(...history.map(h => h.weight));

  // Calculate avg RPE of last 3
  const last3 = history.slice(-3);
  const avgRPELast3 = last3.reduce((sum, h) => sum + h.rpe, 0) / last3.length;

  // Calculate days since last exercise
  const daysSince = (Date.now() - last.date.getTime()) / (1000 * 60 * 60 * 24);

  // Calculate progression rate (weight change per session)
  const progressionRate = history.length >= 2
    ? (last.weight - prev.weight) / prev.weight
    : 0;

  // Calculate volume trend
  const volumeTrend = history.length >= 2
    ? ((last.weight * last.reps) / (prev.weight * prev.reps)) - 1
    : 0;

  return {
    lastWeight: last.weight / maxWeight,
    lastReps: last.reps / 20,
    lastRPE: last.rpe / 10,
    avgRPELast3: avgRPELast3 / 10,
    targetReps: targetReps / 20,
    readiness: readiness / 100,
    setsCompletedToday: Math.min(setsCompletedToday, 20) / 20,
    daysSinceExercise: Math.min(daysSince, 14) / 14,
    progressionRate: Math.max(-1, Math.min(1, progressionRate)), // Clamp to -1 to 1
    volumeTrend: Math.max(-0.5, Math.min(0.5, volumeTrend)) + 0.5, // Normalize to 0-1
  };
}
